import { MAX_AUDIO_BYTES, MAX_AUDIO_MS, MAX_QUEUED_AUDIO_BYTES_HARD, MAX_QUEUED_AUDIO_ITEMS_HARD, DRAFT_DESCRIPTION_MAX_LENGTH } from "../app/constants";
import { createId, nowIso, sanitizePlainText } from "../app/validation";
import { getDb } from "./database";
import { buildMediaRecord, getPreferredMediaStorageFormat, prepareMediaPayload, shouldRetryMediaStorageAsArrayBuffer } from "./mediaRepo";
import type { MediaStorageFormat, TranscriptionQueueJob } from "./schema";
import type { TranscriptionResultInput } from "./reportRepo";
import { isRecordingPending, projectRecordings } from "./recordingState";

export async function saveIssueAudio(issueId: string, audio: Blob, mimeType: string, durationMs: number, recordingId?: string): Promise<void> {
  if (!audio.size || audio.size > MAX_AUDIO_BYTES || !Number.isFinite(durationMs) || durationMs < 0 || durationMs > MAX_AUDIO_MS || !mimeType.startsWith("audio/")) {
    throw new Error("This recording could not be saved. Record a shorter voice note and try again.");
  }
  const jobId = recordingId ?? createId("transcription_job");
  const audioBlobKey = createId("media_audio");
  const preferred = await getPreferredMediaStorageFormat();
  const save = async (format: MediaStorageFormat) => {
    const payload = await prepareMediaPayload(audio, mimeType, format);
    const db = await getDb();
    const tx = db.transaction(["issues", "mediaBlobs", "transcriptionQueue", "reports"], "readwrite", { durability: "strict" });
    const issueStore = tx.objectStore("issues");
    const queue = tx.objectStore("transcriptionQueue");
    const issue = await issueStore.get(issueId);
    if (!issue) { await tx.done; throw new Error("The photo was removed before this recording could be saved."); }
    const existing = await queue.get(jobId);
    if (existing) {
      await tx.done;
      if (existing.issueId !== issueId) throw new Error("This recording belongs to another photo.");
      return;
    }
    const all = await queue.getAll();
    const pending = all.filter(isRecordingPending);
    let bytes = 0;
    for (const item of all) {
      if (item.audioDeletedAt || item.status === "canceled" || item.status === "superseded") continue;
      bytes += item.audioSizeBytes ?? (await tx.objectStore("mediaBlobs").get(item.audioBlobKey))?.sizeBytes ?? 0;
    }
    if (pending.length >= MAX_QUEUED_AUDIO_ITEMS_HARD || bytes + audio.size > MAX_QUEUED_AUDIO_BYTES_HARD) {
      await tx.done; throw new Error("Saved recordings have reached this device's queue limit. Your existing recordings are safe.");
    }
    const now = nowIso();
    const voiceBatchId = issue.voiceBatchId && !issue.approvedAt ? issue.voiceBatchId : createId("voice_batch");
    const old = all.filter((job) => job.issueId === issueId);
    // Upgrade the old single-recording outbox without dropping its audio or ID.
    for (const job of old.filter((item) => !item.voiceBatchId && isRecordingPending(item))) {
      job.voiceBatchId = voiceBatchId;
      job.audioMimeType ??= issue.audioMimeType;
      job.audioDurationMs ??= issue.audioDurationMs;
      job.audioSizeBytes ??= issue.audioSizeBytes;
      await queue.put(job);
    }
    const audioVersion = Math.max(issue.audioVersion ?? 0, ...old.map((job) => job.audioVersion)) + 1;
    const job: TranscriptionQueueJob = {
      jobId, issueId, reportId: issue.reportId, audioBlobKey, audioVersion, voiceBatchId,
      audioMimeType: mimeType, audioDurationMs: durationMs, audioSizeBytes: audio.size,
      createdAt: now, updatedAt: now, attempts: 0, nextAttemptAt: now, status: "queued", stage: "saved"
    };
    await tx.objectStore("mediaBlobs").put(buildMediaRecord({ key: audioBlobKey, reportId: issue.reportId, roomId: issue.roomId, issueId, kind: "audio", payload, createdAt: now }));
    await queue.put(job);
    await issueStore.put(projectRecordings({
      ...issue, audioVersion, voiceBatchId,
      voiceBaseDescription: voiceBatchId === issue.voiceBatchId ? issue.voiceBaseDescription : issue.description,
      approvedAt: undefined, pendingAiDescription: undefined, pendingSuggestedDescription: undefined,
      pendingTranscript: undefined, pendingCandidateJobId: undefined, transcriptionStatus: "queued"
    }, [...old, job], now));
    const report = await tx.objectStore("reports").get(issue.reportId);
    if (report) await tx.objectStore("reports").put({ ...report, updatedAt: now });
    await tx.done;
  };
  try { await save(preferred); }
  catch (error) { if (!shouldRetryMediaStorageAsArrayBuffer(error, preferred)) throw error; await save("array_buffer"); }
}

export async function applyTranscriptionResult(input: TranscriptionResultInput): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["issues", "transcriptionQueue", "reports"], "readwrite", { durability: "strict" });
  const queue = tx.objectStore("transcriptionQueue");
  const job = await queue.get(input.jobId);
  if (!job || job.issueId !== input.issueId || !isRecordingPending(job)) { await tx.done; return; }
  const issue = await tx.objectStore("issues").get(job.issueId);
  if (!issue || issue.reportId !== job.reportId) { await tx.done; return; }
  const now = nowIso();
  const transcript = sanitizePlainText(input.transcript ?? input.description ?? "", DRAFT_DESCRIPTION_MAX_LENGTH);
  const suggested = sanitizePlainText(input.suggestedDescription ?? input.description ?? transcript, DRAFT_DESCRIPTION_MAX_LENGTH);
  const next: TranscriptionQueueJob = {
    ...job, status: input.needsManualEntry || !suggested ? "error" : "completed",
    stage: input.needsManualEntry || !suggested ? "error" : "ready",
    transcript, suggestedDescription: suggested, modelUsed: input.modelUsed,
    lastError: input.needsManualEntry || !suggested ? "No clear speech was found. The recording is still saved; retry or remove this recording." : undefined,
    completedAt: now, updatedAt: now, lockOwner: undefined, lockExpiresAt: undefined
  };
  await queue.put(next);
  await tx.objectStore("issues").put(projectRecordings(issue, await queue.index("byIssueId").getAll(job.issueId), now));
  const report = await tx.objectStore("reports").get(job.reportId);
  if (report) await tx.objectStore("reports").put({ ...report, updatedAt: now });
  await tx.done;
}

export async function claimNextDueQueueJob(reportId: string | undefined, workerId: string, now = new Date()): Promise<TranscriptionQueueJob | undefined> {
  const db = await getDb();
  const tx = db.transaction(["issues", "transcriptionQueue"], "readwrite");
  const queue = tx.objectStore("transcriptionQueue");
  const jobs = reportId ? await queue.index("byReportId").getAll(reportId) : await queue.getAll();
  const time = now.toISOString();
  const due = jobs.filter((job) => isRecordingPending(job) && job.status !== "error" && job.nextAttemptAt <= time &&
    (!job.lockExpiresAt || job.lockExpiresAt <= time)).sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.audioVersion - b.audioVersion || a.jobId.localeCompare(b.jobId));
  for (const job of due) {
    const issue = await tx.objectStore("issues").get(job.issueId);
    if (!issue || issue.reportId !== job.reportId) { await queue.put({ ...job, status: "canceled", updatedAt: time }); continue; }
    const claimed = { ...job, lockOwner: workerId, lockExpiresAt: new Date(now.getTime() + 120_000).toISOString(), updatedAt: time };
    await queue.put(claimed);
    await tx.done;
    return claimed;
  }
  await tx.done;
  return undefined;
}

async function updateJob(jobId: string, patch: Partial<TranscriptionQueueJob>, onlyIfUnlocked = false): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["issues", "transcriptionQueue"], "readwrite");
  const queue = tx.objectStore("transcriptionQueue");
  const job = await queue.get(jobId);
  if (!job || !isRecordingPending(job)) { await tx.done; return; }
  if (onlyIfUnlocked && job.lockExpiresAt && job.lockExpiresAt > nowIso()) { await tx.done; return; }
  const issue = await tx.objectStore("issues").get(job.issueId);
  if (!issue) { await tx.done; return; }
  const now = nowIso();
  await queue.put({ ...job, ...patch, updatedAt: now });
  await tx.objectStore("issues").put(projectRecordings(issue, await queue.index("byIssueId").getAll(job.issueId), now));
  await tx.done;
}

export const markQueueJobUploading = (jobId: string, progress?: { loadedBytes?: number; totalBytes?: number }) => updateJob(jobId, {
  status: "uploading", stage: "uploading", uploadLoadedBytes: progress?.loadedBytes, uploadTotalBytes: progress?.totalBytes
});
export const markQueueJobTranscribing = (jobId: string) => updateJob(jobId, { status: "transcribing", stage: "transcribing" });
export const markQueueJobError = (jobId: string, message: string) => updateJob(jobId, {
  status: "error", stage: "error", lastError: message, lockOwner: undefined, lockExpiresAt: undefined
});
export const scheduleQueueRetry = (jobId: string, message: string, attempts: number) => updateJob(jobId, {
  status: "retry_wait", stage: "retry_wait", attempts, lastError: message,
  nextAttemptAt: new Date(Date.now() + Math.min(300_000, 2000 * 2 ** Math.min(attempts, 8)) + Math.random() * 1000).toISOString(),
  lockOwner: undefined, lockExpiresAt: undefined
});

export async function retryRecording(jobId: string): Promise<void> {
  await updateJob(jobId, { status: "queued", stage: "queued", attempts: 0, nextAttemptAt: nowIso(), lastError: undefined, lockOwner: undefined, lockExpiresAt: undefined }, true);
}

export async function forceQueueRetry(issueId: string): Promise<void> {
  const jobs = await (await getDb()).getAllFromIndex("transcriptionQueue", "byIssueId", issueId);
  for (const job of jobs.filter(isRecordingPending)) await retryRecording(job.jobId);
}

export async function cancelRecording(jobId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(["issues", "transcriptionQueue", "mediaBlobs", "reports"], "readwrite", { durability: "strict" });
  const queue = tx.objectStore("transcriptionQueue");
  const job = await queue.get(jobId);
  if (!job || job.status === "canceled") { await tx.done; return; }
  const now = nowIso();
  await queue.put({ ...job, status: "canceled", updatedAt: now, lockOwner: undefined, lockExpiresAt: undefined });
  await tx.objectStore("mediaBlobs").delete(job.audioBlobKey);
  const issue = await tx.objectStore("issues").get(job.issueId);
  if (issue) await tx.objectStore("issues").put(projectRecordings(issue, await queue.index("byIssueId").getAll(job.issueId), now));
  const report = await tx.objectStore("reports").get(job.reportId);
  if (report) await tx.objectStore("reports").put({ ...report, updatedAt: now });
  await tx.done;
}

export async function cancelIssueVoiceNote(issueId: string): Promise<void> {
  const jobs = await (await getDb()).getAllFromIndex("transcriptionQueue", "byIssueId", issueId);
  for (const job of jobs.filter(isRecordingPending)) await cancelRecording(job.jobId);
}

export async function repairReportTranscriptionJobs(reportId?: string): Promise<boolean> {
  const db = await getDb();
  const tx = db.transaction(["issues", "transcriptionQueue"], "readwrite", { durability: "strict" });
  const queue = tx.objectStore("transcriptionQueue");
  const issues = reportId ? await tx.objectStore("issues").index("byReportId").getAll(reportId) : await tx.objectStore("issues").getAll();
  let changed = false;
  for (const issue of issues) {
    const jobs = await queue.index("byIssueId").getAll(issue.id);
    const pending = jobs.filter(isRecordingPending);
    if (issue.audioBlobKey && !jobs.some((job) => job.audioBlobKey === issue.audioBlobKey)) {
      pending.push({ jobId: issue.activeTranscriptionJobId ?? createId("transcription_job"), issueId: issue.id, reportId: issue.reportId,
        audioBlobKey: issue.audioBlobKey, audioVersion: issue.audioVersion || 1, createdAt: issue.updatedAt, updatedAt: nowIso(),
        attempts: 0, nextAttemptAt: nowIso(), status: "queued", stage: "saved" });
    }
    if (!pending.length || (issue.voiceBatchId && pending.every((job) => job.voiceBatchId))) continue;
    const voiceBatchId = issue.voiceBatchId ?? createId("voice_batch");
    for (const job of pending) {
      job.voiceBatchId ??= voiceBatchId; job.audioMimeType ??= issue.audioMimeType;
      job.audioDurationMs ??= issue.audioDurationMs; job.audioSizeBytes ??= issue.audioSizeBytes;
      await queue.put(job);
    }
    await tx.objectStore("issues").put(projectRecordings({ ...issue, voiceBatchId, approvedAt: undefined, voiceBaseDescription: issue.description }, pending, nowIso()));
    changed = true;
  }
  await tx.done;
  return changed;
}
